// -
// VELLA API SERVICE
// All backend communication in one place
// -

import { getAspectRatio as _defaultAspect } from "./lib/aspectRatioMatrix";
import { getCurrentLanguage } from "./i18n/i18n";

const API = process.env.REACT_APP_API_URL || "https://vella-backend-production.up.railway.app";
export const VELLA_VOICE_ID = "T720RsqorTx4ZZWohrNN";

// ─── Thinking-state tracker ──────────────────────────────────
// Counts in-flight AI requests so the LivingVellaBackground can
// brighten / pulse harder while Vella is "thinking".
let _thinkingCount = 0;
function _setThinkingClass(on) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("vella-thinking", !!on);
}
export function pushThinking() {
  _thinkingCount++;
  if (_thinkingCount === 1) _setThinkingClass(true);
}
export function popThinking() {
  _thinkingCount = Math.max(0, _thinkingCount - 1);
  if (_thinkingCount === 0) _setThinkingClass(false);
}

// - Token Management -
// Guarded so Safari private mode / quota-exceeded errors don't crash
// the app on first auth check. Falls back to an in-memory token for
// the lifetime of the tab when localStorage is unavailable.
let _memoryToken = null;
function getToken() {
  try { return localStorage.getItem("vella_token") || _memoryToken; }
  catch { return _memoryToken; }
}
function setToken(t) {
  _memoryToken = t || null;
  try { if (t) localStorage.setItem("vella_token", t); }
  catch (e) { console.warn("[api] localStorage unavailable, using in-memory token:", e?.message); }
}
function clearToken() {
  _memoryToken = null;
  try { localStorage.removeItem("vella_token"); } catch {}
}

// - Simple GET cache (30s TTL) to prevent duplicate fetches -
const _cache = new Map();
function getCached(key) {
  const c = _cache.get(key);
  if (c && Date.now() - c.ts < 30000) return c.data;
  return null;
}
function setCache(key, data) {
  _cache.set(key, { data, ts: Date.now() });
  if (_cache.size > 100) _cache.delete(_cache.keys().next().value);
}

// - Request deduplication: coalesce identical in-flight GETs -
const _inflight = new Map();

// - Base Fetch with Auth -
async function api(path, options = {}) {
  const isGet = !options.method || options.method === "GET";

  // Return cached data if available
  if (isGet) {
    const cached = getCached(path);
    if (cached) return cached;
    // Coalesce duplicate in-flight requests
    if (_inflight.has(path)) return _inflight.get(path);
  }

  const token = getToken();
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // Forward the active UI language to the backend on every request.
  // Backend reads this in routes/ai.js so Vella's brain can reply in the
  // user's language without depending on a per-call body field.
  const _lang = getCurrentLanguage?.();
  if (_lang && !headers["Accept-Language"]) headers["Accept-Language"] = _lang;

  const request = (async () => {
    let res;
    try {
      res = await fetch(`${API}${path}`, { ...options, headers });
    } catch (networkErr) {
      // Fetch-level failure — DNS, offline, CORS, abort. Wrap so callers
      // can distinguish from server errors.
      const err = new Error(`Network error: ${networkErr?.message || "fetch failed"}`);
      err.kind = "network";
      err.path = path;
      err.cause = networkErr;
      throw err;
    }

    // Parse the body once. If the server returned non-JSON (Cloudflare
    // 502 HTML page, Railway timeout, etc.) we still want to surface a
    // useful message rather than swallowing a JSON.parse failure as a
    // generic "trouble connecting".
    let data = null;
    let rawBody = "";
    try {
      rawBody = await res.text();
      if (rawBody) data = JSON.parse(rawBody);
    } catch {
      data = null; // body wasn't JSON
    }

    if (res.status === 401) {
      // Show a toast before tearing the page down so the user knows why
      // they're being kicked. Listener lives in App.jsx (mount-time
      // window.addEventListener("vella:session-expired", …)). Hard reload
      // happens on a short delay so the toast actually paints.
      clearToken();
      try {
        window.dispatchEvent(new CustomEvent("vella:session-expired", {
          detail: { path, reason: data?.error || "Session expired" },
        }));
      } catch { /* CustomEvent unsupported — fall through to reload */ }
      setTimeout(() => { try { window.location.reload(); } catch {} }, 1500);
      const err = new Error("Session expired");
      err.kind = "auth";
      err.status = 401;
      err.path = path;
      err.body = data;
      throw err;
    }

    if (!res.ok) {
      // Build a structured error so callers (askVella, TalkToVella, etc.)
      // can render kind-specific copy and surface a request id for support.
      // Fields:
      //   .status     HTTP status code (e.g., 503)
      //   .code       backend-provided machine-readable code (e.g.,
      //               "user_kill_switch_active", "subscription_required")
      //   .body       parsed JSON body (when present)
      //   .rawBody    original text (when JSON parse failed)
      //   .path       which request failed
      //   .requestId  X-Request-Id from the response (matches server logs)
      //   .kind       coarse bucket: "auth"|"subscription"|"rate_limit"|
      //               "kill_switch"|"server"|"client"
      const code = data?.code || data?.error || null;
      const message = data?.message || data?.error || (rawBody ? rawBody.slice(0, 200) : `HTTP ${res.status}`);
      let kind;
      if (res.status === 402) kind = "subscription";
      else if (res.status === 429) kind = "rate_limit";
      else if (code === "user_kill_switch_active" || code === "global_kill_switch_active") kind = "kill_switch";
      else if (res.status >= 500) kind = "server";
      else kind = "client";
      const err = new Error(message);
      err.status = res.status;
      err.code = code;
      err.body = data;
      err.rawBody = rawBody;
      err.path = path;
      err.requestId = res.headers.get("X-Request-Id") || null;
      err.kind = kind;
      throw err;
    }

    if (isGet) setCache(path, data);
    return data;
  })();

  if (isGet) {
    _inflight.set(path, request);
    request.finally(() => _inflight.delete(path));
  }

  return request;
}

// -
// AUTH
// -

export async function signup(email, password, name) {
  const data = await api("/api/auth/signup", {
    method: "POST",
    body: JSON.stringify({ email, password, name }),
  });
  setToken(data.token);
  return data;
}

export async function login(email, password) {
  const data = await api("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setToken(data.token);
  return data;
}

export function logout() {
  clearToken();
}

export function isLoggedIn() {
  return !!getToken();
}

// - Get current user + business data -
export async function getMe() {
  return api("/api/auth/me");
}

// - Patch the authed user (OnboardingWizard uses this to flip onboardingComplete) -
export async function updateMe(updates) {
  return api("/api/users/me", {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// - Change password (Settings → Account) -
export async function changePassword(currentPassword, newPassword) {
  return api("/api/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

// - GDPR account deletion. Caller must collect the password and a
//   "DELETE" confirmation string before invoking this.
export async function deleteAccount(currentPassword, confirmation) {
  return api("/api/users/me", {
    method: "DELETE",
    body: JSON.stringify({ currentPassword, confirmation }),
  });
}

// - In-app notification feed (header bell). Pass an optional `since`
//   ISO timestamp to filter to anything newer than the last seen marker.
export async function getNotificationsFeed({ since, limit } = {}) {
  const params = new URLSearchParams();
  if (since) params.set("since", since);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return api(`/api/notifications/feed${qs ? `?${qs}` : ""}`);
}

// - Stripe invoices for the Settings → Billing card -
export async function getInvoices({ limit } = {}) {
  const qs = limit ? `?limit=${limit}` : "";
  return api(`/api/billing/invoices${qs}`);
}

// - Open the printable monthly report in a new tab. Token is passed via
//   query string because window.open() can't carry an Authorization header.
export function openMonthlyReport({ days = 30, autoPrint = true } = {}) {
  const token = getToken();
  if (!token) return null;
  const params = new URLSearchParams({ token, days: String(days) });
  if (autoPrint) params.set("print", "1");
  const url = `${API}/api/reports/monthly?${params}`;
  try { window.open(url, "_blank", "noopener,noreferrer"); } catch {}
  return url;
}

// -
// BUSINESS PROFILE
// -

export async function saveBusiness(data) {
  return api("/api/auth/business", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// -
// HEALTH PROBE — used by the admin uptime indicator. Bypasses the
// authenticated `api()` helper so a logged-out admin viewing the page
// (or a 401 elsewhere) doesn't poison the uptime read. Short timeout
// so a hung backend flips to red quickly instead of hanging the UI.
// -
export async function getHealth({ timeoutMs = 4000 } = {}) {
  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const t = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(`${API}/health`, {
      method: "GET",
      cache: "no-store",
      signal: ctrl?.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    if (t) clearTimeout(t);
  }
}

export async function getBusiness() {
  return api("/api/auth/business");
}

// -
// AI (Secure -- all calls go through backend)
// -

// Conversation history — best-effort. Backend endpoint may not exist
// yet; the conversation store treats failures as a signal to stay on
// localStorage. Returns whatever the server returns (expected shape:
// { conversations: [{ id, title, preview, updated_at }] }).
export async function getConversations() {
  return api("/api/conversations");
}

// Create a conversation server-side. `data` may include { id, title,
// preview, project_id }. Best-effort — the sidebar fires this and keeps
// working from localStorage if the backend rejects or is absent.
export async function createConversation(data = {}) {
  return api("/api/conversations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Rename / re-parent a conversation. `updates` may include { title,
// project_id }.
export async function updateConversation(id, updates = {}) {
  return api(`/api/conversations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// Delete a single conversation server-side.
export async function deleteConversationRemote(id) {
  return api(`/api/conversations/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// Returns { reply, structured_preview } — preserve the full response so
// callers can render a platform-accurate preview when the backend includes one.
// Older callers that only used `data.reply` continue to work via destructuring.
export async function aiChat(message, history = [], opts = {}) {
  const { activeWorkflows, signal } = opts || {};
  pushThinking();
  try {
    const body = { message, history, language: getCurrentLanguage?.() };
    if (Array.isArray(activeWorkflows) && activeWorkflows.length > 0) {
      body.activeWorkflows = activeWorkflows;
    }
    const data = await api("/api/ai/chat", {
      method: "POST",
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
    // /chat returns widgets too (website/creative cards) — surface them so the
    // askVella retry path can preserve previews, not just the reply string.
    return { reply: data.reply, structured_preview: data.structured_preview || null, widgets: data.widgets || null };
  } finally { popThinking(); }
}

// Full agentic brain endpoint -- single-agent with tools + auto-swarm
// routing. Returns { reply, widgets, mode, model }. Used for high-intent
// requests (e.g. reverse-engineering a competitor ad) that benefit from
// the brain's tool use over the lighter /chat path.
export async function aiBrain(message, history = [], opts = {}) {
  const { activeWorkflows } = opts || {};
  pushThinking();
  try {
    const body = { message, history, language: getCurrentLanguage?.() };
    if (Array.isArray(activeWorkflows) && activeWorkflows.length > 0) {
      body.activeWorkflows = activeWorkflows;
    }
    const data = await api("/api/ai/brain", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return {
      reply: data.reply,
      widgets: data.widgets || null,
      mode: data.mode || null,
      model: data.model || null,
    };
  } finally { popThinking(); }
}

export async function aiGeneratePost(platform, postType, tone, topic, hasImage) {
  const data = await api("/api/ai/generate-post", {
    method: "POST",
    body: JSON.stringify({ platform, postType, tone, topic, hasImage }),
  });
  return data.post;
}

export async function aiGenerateCampaign(platform, businessName, description, objective, location) {
  const data = await api("/api/ai/generate-campaign", {
    method: "POST",
    body: JSON.stringify({ platform, businessName, description, objective, location }),
  });
  return data.campaign;
}

export async function aiGenerateReply(platform, type, author, message) {
  const data = await api("/api/ai/generate-reply", {
    method: "POST",
    body: JSON.stringify({ platform, type, author, message }),
  });
  return data.reply;
}

export async function aiAnalyze(question, campaignData) {
  const data = await api("/api/ai/analyze", {
    method: "POST",
    body: JSON.stringify({ question, data: campaignData }),
  });
  return data.analysis;
}

// Generic AI call -- for SEO tools, AI SEO, custom prompts.
// Returns { reply, structured_preview } so the frontend can render a
// platform-accurate preview alongside the chat reply when the backend ships one.
export async function aiCustom(systemPrompt, message, history = [], opts = {}) {
  const { activeWorkflows, signal, attachments } = opts || {};
  pushThinking();
  try {
    const body = { systemPrompt, message, history, language: getCurrentLanguage?.() };
    if (Array.isArray(activeWorkflows) && activeWorkflows.length > 0) {
      body.activeWorkflows = activeWorkflows;
    }
    // Vision / document input: pass image|PDF attachments through to the
    // backend, which converts them to Claude content blocks
    // (services/claude-capabilities.js → buildContentFromBody). Accepts data
    // URLs ("data:image/png;base64,…"), { data, media_type }, or { url }.
    if (Array.isArray(attachments) && attachments.length > 0) {
      body.attachments = attachments;
    }
    const data = await api("/api/ai/custom", {
      method: "POST",
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
    return {
      reply: data.reply,
      structured_preview: data.structured_preview || null,
      widgets: data.widgets || null,
      // Cognitive-router metadata + which model actually answered (after any
      // Opus→Sonnet→Haiku fallback). Surfaced so the UI can show the brain
      // path / responding model. Older callers ignore the extra fields.
      routing: data.routing || null,
      model: data.model || null,
    };
  } finally { popThinking(); }
}

// Streaming chat client for POST /api/ai/stream (Server-Sent Events).
// The backend runs the full agentic tool loop, then streams the reply as SSE.
// Wire format is `event: <name>\ndata: <json>\n\n` — the event TYPE is on the
// `event:` line, NOT inside the JSON payload — so we track the current event
// name as we parse. Events: meta ({model, routing}), delta ({text}),
// done ({reply, widgets, structured_preview, routing, model}), error ({message}).
//
// Usage:
//   await aiStream(text, {
//     systemPrompt, history, attachments, signal,
//     onMeta:  (m) => {},      // routing/model up front
//     onDelta: (chunk) => {},  // append text as it streams
//     onDone:  (final) => {},  // full reply + widgets + preview
//     onError: (err) => {},
//   });
// Returns the final `done` payload (or null if the stream errored/aborted).
export async function aiStream(message, opts = {}) {
  const {
    systemPrompt = "", history = [], attachments, signal, language,
    onMeta, onDelta, onDone, onError,
  } = opts || {};
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const lang = language || getCurrentLanguage?.();
  if (lang) headers["Accept-Language"] = lang;

  const body = { systemPrompt, message, history };
  if (Array.isArray(attachments) && attachments.length > 0) body.attachments = attachments;

  pushThinking();
  let res;
  try {
    res = await fetch(`${API}/api/ai/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (networkErr) {
    popThinking();
    const err = new Error(`Network error: ${networkErr?.message || "fetch failed"}`);
    err.kind = "network";
    if (onError) onError(err);
    throw err;
  }

  if (res.status === 401) {
    popThinking();
    clearToken();
    try { window.dispatchEvent(new CustomEvent("vella:session-expired", { detail: { path: "/api/ai/stream", reason: "Session expired" } })); } catch {}
    setTimeout(() => { try { window.location.reload(); } catch {} }, 1500);
    const err = new Error("Session expired"); err.kind = "auth"; err.status = 401;
    if (onError) onError(err);
    throw err;
  }
  if (!res.ok || !res.body) {
    popThinking();
    const text = await res.text().catch(() => "");
    const err = new Error(`Stream failed (HTTP ${res.status}): ${text.slice(0, 200) || "no body"}`);
    err.status = res.status;
    err.kind = res.status >= 500 ? "server" : "client";
    if (onError) onError(err);
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let lastDone = null;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE events are separated by a blank line. Keep the trailing partial.
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop();
      for (const chunk of chunks) {
        let name = "message";
        let dataStr = "";
        for (const line of chunk.split("\n")) {
          if (line.startsWith("event:")) name = line.slice(6).trim();
          else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
        }
        if (!dataStr) continue;
        let data;
        try { data = JSON.parse(dataStr); } catch { continue; }
        if (name === "meta") { if (onMeta) onMeta(data); }
        else if (name === "delta") { if (onDelta) onDelta(data.text || ""); }
        else if (name === "done") { lastDone = data; if (onDone) onDone(data); }
        else if (name === "error") { if (onError) onError(new Error(data.message || "stream error")); }
      }
    }
  } finally {
    popThinking();
  }
  return lastDone;
}

// Award-winning website generator. Sends the structured brief to the
// dedicated /api/ai/generate-website endpoint, which uses a heavily-tuned
// system prompt (GSAP + Lenis + pinned scroll + parallax + palette + fonts).
// Returns { html, model, brief }.
export async function aiGenerateWebsite(brief) {
  pushThinking();
  try {
    const data = await api("/api/ai/generate-website", {
      method: "POST",
      body: JSON.stringify({ brief }),
    });
    return {
      html: data.html || "",
      model: data.model || null,
      brief: data.brief || brief,
    };
  } finally { popThinking(); }
}

// Publish raw generated HTML to a live Vercel URL via the backend
// /api/webstudio/deploy-html endpoint. Returns { success, url, ... }.
// Throws a structured error (402 = paid plan required) on failure.
export async function deployWebsiteHtml(html, siteName) {
  return api("/api/webstudio/deploy-html", {
    method: "POST",
    body: JSON.stringify({ html, siteName }),
  });
}

// Returns { reply, agents, widgets, structured_preview }
// agents: Array<{ agentName, role, contribution, confidence }>
export async function aiSwarm(systemPrompt, message, history = []) {
  pushThinking();
  try {
    const data = await api("/api/ai/swarm", {
      method: "POST",
      body: JSON.stringify({ systemPrompt, message, history }),
    });
    return {
      reply: data.reply || data.response || data.text || "",
      agents: data.agents || [],
      widgets: data.widgets || null,
      structured_preview: data.structured_preview || null,
    };
  } finally { popThinking(); }
}

// Upload a file (image, etc.) to the backend for use in AI requests.
// Returns { url, mimeType, size } or throws on error.
export async function uploadFile(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API}/api/ai/upload`, { method: "POST", headers, body: formData });
  if (res.status === 401) { clearToken(); window.location.reload(); throw new Error("Session expired"); }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Upload failed ${res.status}: ${text || "request failed"}`);
  }
  return res.json();
}

// -
// RELAY
// Desktop agent bridge — screen view, click-to-command, command history.
// -

// Returns { connected, device: { platform, hostname, id }, screenshot, url }
export async function getRelayStatus() {
  return api("/api/relay/status");
}

// Send a normalized click (x, y as 0–1 fractions) to the relay agent.
// Returns { screenshot, duration_ms }
export async function sendRelayClick(x, y) {
  return api("/api/relay/click", {
    method: "POST",
    body: JSON.stringify({ x, y }),
  });
}

// Returns { commands: Array<{ id, type, description, status, duration_ms, screenshot_url, ts }> }
export async function getRelayHistory() {
  return api("/api/relay/history");
}

// Send a generic relay command (navigate, type, scroll, etc.)
// Returns { result, screenshot, duration_ms }
export async function sendRelayCommand(command) {
  return api("/api/relay/command", {
    method: "POST",
    body: JSON.stringify({ command }),
  });
}

export async function testCall() {
  return api("/api/outreach/test-call", { method: "POST" });
}

export async function saveDailyBriefing(settings) {
  return api("/api/settings/daily-briefing", {
    method: "POST",
    body: JSON.stringify(settings),
  });
}

// -
// VOICE CALLS (Vella calls you) — backed by /api/voice-schedule
// Scheduled daily call at the user-chosen time, plus manual test-call trigger.
// -

// Resolve the user's id once per session for the :clientId URL segment.
let _cachedClientId = null;
async function _clientId() {
  if (_cachedClientId) return _cachedClientId;
  try {
    const me = await getMe();
    _cachedClientId = me?.id || me?.userId || me?.user?.id || null;
    return _cachedClientId;
  } catch { return null; }
}

export async function getVoiceSchedule() {
  const id = await _clientId();
  if (!id) return { schedule: null };
  return api(`/api/voice-schedule/${encodeURIComponent(id)}`);
}

// settings: { phoneNumber (E.164), callTime ("HH:MM"), timezone, enabled, includeDigest, includeApprovals, voiceId }
export async function saveVoiceSchedule(settings) {
  const id = await _clientId();
  if (!id) throw new Error("Not signed in");
  // Default to the Vella voice unless caller overrides.
  const body = { voiceId: VELLA_VOICE_ID, ...settings };
  return api(`/api/voice-schedule/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function triggerVoiceTestCall(phoneNumber) {
  const id = await _clientId();
  if (!id) throw new Error("Not signed in");
  return api(`/api/voice-schedule/${encodeURIComponent(id)}/test-call`, {
    method: "POST",
    body: JSON.stringify(phoneNumber ? { phoneNumber } : {}),
  });
}

// -
// WIX
// -

export async function getWixConnectUrl() {
  return api("/api/wix/connect");
}

export async function getWixStatus() {
  return api("/api/wix/status");
}

export async function disconnectWix() {
  return api("/api/wix/disconnect", { method: "DELETE" });
}

export async function claimWixConnection(claimCode) {
  return api("/api/wix/claim", { method: "POST", body: JSON.stringify({ claimCode }) });
}

export async function runAutoSeo() {
  return api("/api/wix/auto-seo", { method: "POST" });
}

// -
// NOTIFICATIONS
// -

export async function getNotificationPrefs() {
  return api("/api/notifications/preferences");
}

export async function saveNotificationPrefs(prefs) {
  return api("/api/notifications/preferences", {
    method: "PUT",
    body: JSON.stringify(prefs),
  });
}

// -
// BILLING (Stripe)
// -

export async function getBillingPlans() {
  return api("/api/billing/plans");
}

export async function getBillingStatus() {
  return api("/api/billing/status");
}

export async function createCheckout(plan) {
  return api("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ planId: plan }),
  });
}

export async function buyCredits(packId) {
  return api("/api/billing/buy-credits", {
    method: "POST",
    body: JSON.stringify({ packId }),
  });
}

export async function openBillingPortal() {
  return api("/api/billing/portal", { method: "POST" });
}

export async function checkLimit(type) {
  return api(`/api/billing/check-limit/${type}`);
}

// SOCIAL OAUTH
export async function getOAuthConnectUrl(platform) {
  return api(`/api/oauth/${platform}/connect`);
}

export async function getOAuthStatus() {
  return api("/api/oauth/status");
}

export async function disconnectPlatform(platform) {
  return api(`/api/oauth/${platform}/disconnect`, { method: "DELETE" });
}

// PUBLISHING
export async function publishPost({ platform, caption, hashtags, image }) {
  return api("/api/posts/publish", {
    method: "POST",
    body: JSON.stringify({ platform, caption, hashtags, image }),
  });
}

export async function sendReply({ platform, type, messageId, reply }) {
  return api("/api/inbox/reply", {
    method: "POST",
    body: JSON.stringify({ platform, type, messageId, reply }),
  });
}

export async function syncInbox() {
  return api("/api/inbox/sync", { method: "POST" });
}

export async function getInbox({ platform, type, unreplied, leads } = {}) {
  const params = new URLSearchParams();
  if (platform) params.append("platform", platform);
  if (type) params.append("type", type);
  if (unreplied) params.append("unreplied", "true");
  if (leads) params.append("leads", "true");
  return api(`/api/inbox?${params.toString()}`);
}

// ANALYTICS
export async function getAnalyticsOverview(range = "30d") {
  return api(`/api/analytics/overview?range=${range}`);
}

export async function getPostAnalytics({ platform, limit } = {}) {
  const params = new URLSearchParams();
  if (platform) params.append("platform", platform);
  if (limit) params.append("limit", limit);
  return api(`/api/analytics/posts?${params.toString()}`);
}

export async function getCampaignAnalytics({ platform } = {}) {
  const params = new URLSearchParams();
  if (platform) params.append("platform", platform);
  return api(`/api/analytics/campaigns?${params.toString()}`);
}

// Website live-status + traffic, SEO overview, and AI SEO insights —
// surfaced on the dashboard "Live Website" card and the SEO panels.
// All three endpoints are best-effort: callers default the shape to {}
// so missing keys render as the empty state rather than crashing.
export async function getWebsiteAnalytics() {
  return api("/api/analytics/website");
}

export async function getSeoOverview() {
  return api("/api/seo/overview");
}

export async function getSeoInsights() {
  return api("/api/seo/insights");
}

// -
// DOMAINS — white-labeled search / purchase / connect flow used by
// the Web Studio "Connect Domain" panel. The backend hides the upstream
// registrar entirely; we surface only generic copy.
// -
export async function searchDomains(query) {
  return api("/api/domains/search", {
    method: "POST",
    body: JSON.stringify({ query }),
  });
}

export async function purchaseDomain(domain, years = 1) {
  return api("/api/domains/purchase", {
    method: "POST",
    body: JSON.stringify({ domain, years }),
  });
}

export async function connectDomain(domain) {
  return api("/api/domains/connect", {
    method: "POST",
    body: JSON.stringify({ domain }),
  });
}

export async function listDomains() {
  return api("/api/domains/list");
}

// SCHEDULING
export async function schedulePost({ platform, caption, hashtags, imageUrl, postType, tone, scheduledAt }) {
  return api("/api/schedule", {
    method: "POST",
    body: JSON.stringify({ platform, caption, hashtags, imageUrl, postType, tone, scheduledAt }),
  });
}

export async function getScheduledPosts(platform) {
  const params = platform ? `?platform=${platform}` : "";
  return api(`/api/schedule${params}`);
}

export async function cancelScheduledPost(id) {
  return api(`/api/schedule/${id}`, { method: "DELETE" });
}

export async function deletePost(id) {
  return cancelScheduledPost(id);
}

export async function updateScheduledPost(id, updates) {
  return api(`/api/schedule/${id}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// -
// CREATIVE / IMAGE GENERATION
// Aspect-ratio defaulting routes through the matrix (`_defaultAspect`) so no
// hardcoded "1:1" lives outside aspectRatioMatrix.js — see src/lib/aspectRatioMatrix.js.
export function generateCreative(prompt, model, aspectRatio) {
  return api("/api/creative/generate", {
    method: "POST",
    body: JSON.stringify({ prompt, model, aspectRatio: aspectRatio || _defaultAspect(null, null) }),
  });
}

export function generateVariations(prompt, models) {
  return api("/api/creative/variations", {
    method: "POST",
    body: JSON.stringify({ prompt, models }),
  });
}

export function getCreativeHistory() {
  return api("/api/creative/history");
}

export function saveCreativeFeedback(imageId, feedback) {
  return api("/api/creative/feedback", {
    method: "POST",
    body: JSON.stringify({ imageId, feedback }),
  });
}

// Inpaint a region of a creative. `image` and `mask` are base64-encoded
// PNGs (no data URL prefix). The mask is black/white — white where the
// model should regenerate. Returns { result: <base64 png> }.
export function inpaintCreative({ image, mask, prompt }) {
  return api("/api/creative/inpaint", {
    method: "POST",
    body: JSON.stringify({ image, mask, prompt: prompt || "" }),
  });
}

// -
// INSIGHTS
// -

export async function getInsights() {
  return api("/api/insights");
}

// OUTREACH - Trigger calls
export async function triggerCall() {
  return api("/api/outreach/test-call", { method: "POST" });
}

export async function scheduleCall(delayMinutes) {
  return api("/api/outreach/schedule-call", { method: "POST", body: JSON.stringify({ delayMinutes }) });
}

export async function registerPhone(phone, timezone) {
  return api("/api/sms/phone", { method: "POST", body: JSON.stringify({ phone, timezone: timezone || "America/New_York" }) });
}

// VOICE TTS (ElevenLabs via backend) with pause/interrupt support
// -
let _vellaAudio = null;
let _vellaAudioUrl = null;

export function voiceStop() {
  if (_vellaAudio) {
    _vellaAudio.pause();
    _vellaAudio.currentTime = 0;
    _vellaAudio = null;
  }
  if (_vellaAudioUrl) {
    URL.revokeObjectURL(_vellaAudioUrl);
    _vellaAudioUrl = null;
  }
}

export function voiceSpeak(text, onDone) {
  if (!text) { if (onDone) onDone(); return; }
  voiceStop();
  var clean = text
    .replace(/^[-*_]{3,}$/gm, "")
    .replace(/#{1,6}\s*/g, "")
    .replace(/\*{1,3}([^*]*?)\*{1,3}/g, "$1")
    .replace(/\*{3}/g, "")
    .replace(/\*{2}/g, "")
    .replace(/\*/g, "")
    .replace(/_{1,2}([^_]*?)_{1,2}/g, "$1")
    .replace(/^[\s]*[-*+]\s/gm, "")
    .replace(/^[\s]*\d+\.\s/gm, "")
    .replace(/`{1,3}[^`]*`{1,3}/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[\n\r]+/g, ". ")
    .replace(/\s{2,}/g, " ")
    .replace(/\.\s*\./g, ".")
    .trim();
  if (!clean) { if (onDone) onDone(); return; }
  if (clean.length > 1000) clean = clean.substring(0, 1000);
  var token = getToken();
  var headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = "Bearer " + token;
  fetch(API + "/api/voice/speak", {
    method: "POST",
    headers: headers,
    body: JSON.stringify({ text: clean, voiceId: VELLA_VOICE_ID })
  }).then(function(res) {
    if (!res.ok) throw new Error("TTS API " + res.status);
    // Backend sends binary audio/mpeg directly — no base64 decode needed
    return res.blob();
  }).then(function(blob) {
    if (blob && blob.size > 100) {
      var url = URL.createObjectURL(blob);
      var audio = new Audio(url);
      _vellaAudio = audio;
      _vellaAudioUrl = url;
      audio.onended = function() { _vellaAudio = null; URL.revokeObjectURL(url); _vellaAudioUrl = null; if (onDone) onDone(); };
      audio.onerror = function() { _vellaAudio = null; URL.revokeObjectURL(url); _vellaAudioUrl = null; if (onDone) onDone(); };
      audio.play().catch(function(e) { console.warn("Vella voice play failed:", e); _vellaAudio = null; URL.revokeObjectURL(url); _vellaAudioUrl = null; if (onDone) onDone(); });
    } else {
      console.warn("Vella voice: empty audio response");
      if (onDone) onDone();
    }
  }).catch(function(e) { console.warn("Vella voice fetch failed:", e); if (onDone) onDone(); });
}

// -
// ADMIN
// -

export async function adminGetOverview() {
  return api("/api/admin/overview");
}

export async function adminGetUsers(page = 1, limit = 25, search = "") {
  const params = new URLSearchParams({ page, limit, ...(search && { search }) });
  return api(`/api/admin/users?${params}`);
}

export async function adminGetUser(id) {
  return api(`/api/admin/users/${id}`);
}

export async function adminGetSubscriptions() {
  return api("/api/admin/subscriptions");
}

export async function adminGetStripeFeed() {
  return api("/api/admin/stripe-feed");
}

export async function adminGetActivity(limit = 20) {
  return api(`/api/admin/activity?limit=${limit}`);
}

export async function adminGetAlerts() {
  return api("/api/admin/alerts");
}

export async function adminGetHealth() {
  return api("/api/admin/health");
}

export async function adminGetTopQuestions() {
  return api("/api/admin/top-questions");
}

export async function adminGetRevenueChart() {
  return api("/api/admin/revenue-chart");
}

export async function adminChat(message, history = []) {
  return api("/api/admin/chat", {
    method: "POST",
    body: JSON.stringify({ message, history }),
  });
}

export async function adminGetKillSwitch() {
  return api("/api/admin/kill-switch");
}

export async function adminSetKillSwitch(active, reason = "") {
  return api("/api/admin/kill-switch", {
    method: "POST",
    body: JSON.stringify({ active, reason }),
  });
}

export async function adminSuspendUser(userId, disabled, reason = "") {
  return api(`/api/admin/kill-switch/user/${userId}`, {
    method: "POST",
    body: JSON.stringify({ disabled, reason }),
  });
}
